import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, PauseIcon } from 'lucide-react';
interface AudioCardProps {
  number: string;
  title: string;
  subtitle: string;
  duration: string;
  index: number;
  audioUrl?: string;
  isActive: boolean;
  isDimmed: boolean;
  onToggle: () => void;
  onFinish: () => void;
}
export function AudioCard({
  number,
  title,
  subtitle,
  duration,
  index,
  audioUrl,
  isActive,
  isDimmed,
  onToggle,
  onFinish
}: AudioCardProps) {
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number>();
  const PREVIEW_DURATION = 20; // seconds
  useEffect(() => {
    if (audioUrl && !audioRef.current) {
      const audio = new Audio(audioUrl);
      // Strictly enforce 20 second limit even if tab is inactive
      audio.addEventListener('timeupdate', () => {
        if (audio.currentTime >= PREVIEW_DURATION) {
          audio.pause();
          audio.currentTime = 0;
          setProgress(0);
          onFinish();
        }
      });
      audio.addEventListener('ended', () => {
        audio.currentTime = 0;
        setProgress(0);
        onFinish();
      });
      audioRef.current = audio;
    }
  }, [audioUrl, onFinish]);
  const updateProgress = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      const currentProgress = currentTime / PREVIEW_DURATION * 100;
      if (currentTime >= PREVIEW_DURATION) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setProgress(0);
        onFinish();
      } else {
        setProgress(currentProgress);
        animationRef.current = requestAnimationFrame(updateProgress);
      }
    } else {
      // Fallback simulation if no audio URL
      setProgress(prev => {
        if (prev >= 100) {
          onFinish();
          return 0;
        }
        return prev + 100 / (PREVIEW_DURATION * 20);
      });
    }
  };
  useEffect(() => {
    if (isActive) {
      if (audioRef.current) {
        audioRef.current.play();
        animationRef.current = requestAnimationFrame(updateProgress);
      } else {
        // Simulation interval
        const interval = setInterval(updateProgress, 50);
        return () => clearInterval(interval);
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isActive]);
  const handleToggle = () => {
    if (progress >= 100) {
      setProgress(0);
      if (audioRef.current) audioRef.current.currentTime = 0;
    }
    onToggle();
  };
  // Circular progress
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - progress / 100 * circumference;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true,
    margin: '-20px'
  }} transition={{
    duration: 0.4,
    delay: index * 0.06,
    ease: 'easeOut'
  }} className="flex-shrink-0 w-[156px]" data-id="element-11">
      <div onClick={handleToggle} className={`relative overflow-hidden h-full rounded-xl p-4 flex flex-col items-center text-center gap-3 cursor-pointer select-none transition-all duration-500 ${isActive ? 'bg-gradient-to-b from-white/20 to-[#FDFBF7]/10 backdrop-blur-xl shadow-[0_12px_40px_rgba(201,166,107,0.4),0_4px_12px_rgba(184,115,51,0.15),inset_0_1px_0_rgba(255,255,255,0.6)] border border-[#C9A66B]/40 scale-105' : isDimmed ? 'bg-gradient-to-b from-[#FDFBF7] to-white opacity-40 scale-95 border border-transparent grayscale-[30%]' : 'bg-gradient-to-b from-[#FDFBF7] to-white shadow-[0_4px_16px_rgba(201,166,107,0.12),0_1px_3px_rgba(0,0,0,0.04)] border border-[#C9A66B]/15 scale-100'}`} data-id="element-12">
        {/* Metallic Top Accent Line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7A4A20] via-[#C9A66B] to-[#7A4A20] opacity-70" data-id="element-13" />

        {/* Glass crystal light sweep */}
        {isActive && <div className="absolute inset-0 pointer-events-none z-20" style={{
        background: 'linear-gradient(135deg, transparent 0%, transparent 30%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.2) 55%, transparent 70%, transparent 100%)',
        backgroundSize: '200% 200%',
        animation: 'glassSweep 4s ease-in-out infinite'
      }} data-id="element-14" />}

        {/* Glass inner highlight */}
        {isActive && <div className="absolute inset-0 pointer-events-none z-10 rounded-xl" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)'
      }} data-id="element-15" />}

        {/* Play button with circular progress ring */}
        <div className="relative w-11 h-11 flex items-center justify-center z-30" data-id="element-16">
          <svg className="absolute inset-0 w-11 h-11 -rotate-90" viewBox="0 0 44 44" data-id="element-17">
            <defs data-id="element-18">
              <linearGradient id={`metallic-track-${index}`} x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-19">
                <stop offset="0%" stopColor="#7A4A20" stopOpacity="0.3" data-id="element-20" />
                <stop offset="35%" stopColor="#A0652E" stopOpacity="0.3" data-id="element-21" />
                <stop offset="52%" stopColor="#C9A66B" stopOpacity="0.3" data-id="element-22" />
                <stop offset="68%" stopColor="#A0652E" stopOpacity="0.3" data-id="element-23" />
                <stop offset="100%" stopColor="#7A4A20" stopOpacity="0.3" data-id="element-24" />
              </linearGradient>
              <linearGradient id={`metallic-progress-${index}`} x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-25">
                <stop offset="0%" stopColor="#7A4A20" data-id="element-26" />
                <stop offset="35%" stopColor="#A0652E" data-id="element-27" />
                <stop offset="52%" stopColor="#C9A66B" data-id="element-28" />
                <stop offset="68%" stopColor="#A0652E" data-id="element-29" />
                <stop offset="100%" stopColor="#7A4A20" data-id="element-30" />
              </linearGradient>
            </defs>
            <circle cx="22" cy="22" r={radius} fill="none" stroke={isActive ? `url(#metallic-track-${index})` : '#D4B896'} strokeWidth="2.5" className="transition-colors duration-500" data-id="element-31" />
            <circle cx="22" cy="22" r={radius} fill="none" stroke={`url(#metallic-progress-${index})`} strokeWidth="2.5" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{
            transition: 'stroke-dashoffset 0.05s linear'
          }} data-id="element-32" />
          </svg>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all duration-300 bg-gradient-to-br from-[#B87333] via-[#C9A66B] to-[#A0652E] shadow-[inset_0_1px_2px_rgba(255,255,255,0.3),0_4px_12px_rgba(184,115,51,0.3)] ${isActive ? 'scale-110' : ''}`} data-id="element-33">
            {isActive ? <PauseIcon className="w-3.5 h-3.5 text-white" fill="currentColor" data-id="element-34" /> : <PlayIcon className="w-3.5 h-3.5 text-white ml-0.5" fill="currentColor" data-id="element-35" />}
          </div>
        </div>

        {/* Number */}
        <div className="flex flex-col items-center gap-1 z-30 mt-1" data-id="element-36">
          <span className="text-copper font-playfair font-medium text-xs tracking-[0.2em] uppercase" data-id="element-37">
            {number}
          </span>
          <div className="w-4 h-[1px] bg-copper/30" data-id="element-38" />
        </div>

        {/* Title */}
        <h3 className="font-playfair text-charcoal text-sm font-semibold leading-snug line-clamp-2 min-h-[2.5rem] z-30" data-id="element-39">
          {title}
        </h3>

        {/* Duration label */}
        <span className="font-inter text-[10px] tracking-wide z-30 flex items-center gap-1" data-id="element-40">
          <span className="text-lightCopper" data-id="element-41">{duration}</span>
          <span className="text-lightCopper/50" data-id="element-42">·</span>
          <span className="font-medium bg-gradient-to-r from-[#7A4A20] via-[#C9A66B] to-[#7A4A20] bg-clip-text text-transparent" data-id="element-43">
            preview
          </span>
        </span>
      </div>
    </motion.div>;
}